import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import {
  cancelPlatformInvoice, creditPlatformInvoice, filePlatformInvoice, issuePlatformInvoice, listBillingRuns,
  listPlatformInvoices, runPlatformBilling,
  type PlatformBillingRun, type PlatformInvoiceRow, type PlatformInvoiceStatus,
} from '@/lib/api';

/** The invoices easyDent issues to its own customers, and the monthly sweep that draws them up. */
const BillingPage: React.FC = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<PlatformInvoiceRow[]>([]);
  const [runs, setRuns] = useState<PlatformBillingRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [i, r] = await Promise.all([listPlatformInvoices(), listBillingRuns()]);
      setInvoices(i);
      setRuns(r);
      setError(null);
    } catch {
      setError(t('billing.loadError', "Could not load billing."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (id: string, what: () => Promise<unknown>) => {
    setBusy(id); setError(null);
    try {
      await what();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.response?.data?.title ?? t('billing.failed', 'That did not work.'));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  const blocked = invoices.filter((i) => i.status === 'AwaitingCustomerDetails');
  const rest = invoices.filter((i) => i.status !== 'AwaitingCustomerDetails');

  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold text-body">{t('billing.title', "Billing")}</h1>
      <p className="mb-6 text-sm text-muted">
        What easyDent invoices its own customers — subscriptions, payment fees, wallet credit and
        imaging study fees, on one document per customer per month.
      </p>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}

      <RunsCard runs={runs} onRun={async (from, to) => {
        setBusy('run'); setError(null);
        try {
          await runPlatformBilling(from, to);
          await load();
        } catch (e: any) {
          setError(e?.response?.data?.detail ?? e?.response?.data?.title ?? t('billing.runRefused', 'The run was refused.'));
        } finally { setBusy(null); }
      }} busy={busy === 'run'} />

      {blocked.length > 0 && (
        <Card className="mt-6 border-amber-200">
          <h2 className="mb-1 text-sm font-semibold text-body">{t('billing.awaitingCustomer', "Waiting on the customer")}</h2>
          {/* Money already moved for these. The invoice exists so it is visible, not absent. */}
          <p className="mb-3 text-xs text-muted">
            We have taken the money but cannot yet name the buyer on a compliant invoice. Each one
            says which fields are missing.
          </p>
          <InvoiceTable rows={blocked} busy={busy} onAct={act} />
        </Card>
      )}

      <Card className="mt-6">
        <h2 className="mb-3 text-sm font-semibold text-body">{t('billing.invoices', "Invoices")}</h2>
        {rest.length === 0
          ? <p className="text-sm text-subtle-ink">{t('billing.empty', "Nothing yet. Run a period above to draw them up.")}</p>
          : <InvoiceTable rows={rest} busy={busy} onAct={act} />}
      </Card>
    </div>
  );
};

/**
 * The monthly sweep. Drafts only — issuing claims fiscal numbers and stays a deliberate act, because
 * a job that numbered its output would burn a number every time it ran.
 */
const RunsCard: React.FC<{
  runs: PlatformBillingRun[];
  busy: boolean;
  onRun: (from: string, to: string) => Promise<void>;
}> = ({ runs, busy, onRun }) => {
  const { t } = useTranslation();
  // Last month, which is the only period a run is allowed to cover before it has finished.
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(iso(start));
  const [to, setTo] = useState(iso(end));
  const field = 'rounded-lg border border-line-strong px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold text-body">{t('billing.drawUpPeriod', "Draw up a period")}</h2>
      <p className="mb-3 text-xs text-muted">
        Runs automatically a few days into each month — Stripe settles and retries over several days,
        so billing at midnight would strand the receipts that land on the 2nd. This is the same sweep,
        by hand. It drafts; nothing is sent until you issue it.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-xs text-muted-strong">
          From<input type="date" className={field} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          To<input type="date" className={field} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <Button size="sm" loading={busy} onClick={() => onRun(from, to)}>{t('billing.drawUpDrafts', "Draw up drafts")}</Button>
      </div>

      {runs.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-subtle-ink">
              <tr>
                <th className="py-1.5 pr-4">{t('billing.period', "Period")}</th>
                <th className="py-1.5 pr-4">{t('billing.customers', "Customers")}</th>
                <th className="py-1.5 pr-4">{t('billing.drafted', "Drafted")}</th>
                <th className="py-1.5 pr-4">{t('billing.blocked', "Blocked")}</th>
                <th className="py-1.5 pr-4">{t('billing.failed', "Failed")}</th>
                <th className="py-1.5">{t('billing.finished', "Finished")}</th>
              </tr>
            </thead>
            <tbody className="text-body-soft">
              {runs.slice(0, 12).map((r) => (
                <tr key={r.id} className="border-t border-line-soft">
                  <td className="py-1.5 pr-4">{r.periodStart} → {r.periodEnd}</td>
                  <td className="py-1.5 pr-4">{r.customersConsidered}</td>
                  <td className="py-1.5 pr-4">{r.invoicesDrafted}</td>
                  <td className="py-1.5 pr-4">
                    {r.invoicesBlocked > 0
                      ? <span className="text-amber-600">{r.invoicesBlocked}</span>
                      : '0'}
                  </td>
                  <td className="py-1.5 pr-4">
                    {r.failures > 0 ? <span className="text-red-600">{r.failures}</span> : '0'}
                  </td>
                  {/* A run with no finish time is one that died half way — worth seeing as such. */}
                  <td className="py-1.5 text-muted">
                    {r.completedAt ? new Date(r.completedAt).toLocaleString() : 'did not finish'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

const STATUS_STYLE: Record<PlatformInvoiceStatus, string> = {
  Draft: 'bg-subtle text-muted-strong',
  AwaitingCustomerDetails: 'bg-amber-100 text-amber-700',
  Issued: 'bg-brand-50 text-brand-ink-strong',
  Submitted: 'bg-blue-100 text-blue-700',
  Filed: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Failed: 'bg-red-100 text-red-700',
  Cancelled: 'bg-subtle text-subtle-ink',
};

/**
 * Invoice status labels.
 *
 * A function of `t` rather than a module-level constant: a constant is evaluated once at import,
 * so it would freeze whatever language loaded first and never follow a switch. Statuses are also
 * the one column an operator scans, so leaving them untranslated defeats the point.
 */
const statusLabels = (t: TFunction): Record<PlatformInvoiceStatus, string> => ({
  Draft: t('billing.status.draft', 'Draft'),
  AwaitingCustomerDetails: t('billing.status.needsDetails', 'Needs details'),
  Issued: t('billing.status.issued', 'Issued'),
  Submitted: t('billing.status.atAnaf', 'At ANAF'),
  Filed: t('billing.status.filed', 'Filed'),
  Rejected: t('billing.status.rejected', 'Rejected'),
  Failed: t('billing.status.failed', 'Failed'),
  Cancelled: t('billing.status.withdrawn', 'Withdrawn'),
});

const InvoiceTable: React.FC<{
  rows: PlatformInvoiceRow[];
  busy: string | null;
  onAct: (id: string, what: () => Promise<unknown>) => Promise<void>;
}> = ({ rows, busy, onAct }) => {
  const { t } = useTranslation();
  return (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wide text-subtle-ink">
        <tr>
          <th className="py-1.5 pr-4">{t('billing.reference', "Reference")}</th>
          <th className="py-1.5 pr-4">{t('billing.customer', "Customer")}</th>
          <th className="py-1.5 pr-4">{t('billing.period', "Period")}</th>
          <th className="py-1.5 pr-4 text-right">{t('billing.total', "Total")}</th>
          <th className="py-1.5 pr-4">{t('billing.statusHeader', 'Status')}</th>
          <th className="py-1.5" />
        </tr>
      </thead>
      <tbody className="text-body-soft">
        {rows.map((i) => (
          <React.Fragment key={i.id}>
            <tr className="border-t border-line-soft">
              <td className="py-2 pr-4 font-medium">
                {i.reference}
                {i.kind === 'CreditNote' && <span className="ml-1 text-xs text-subtle-ink">credit note</span>}
              </td>
              <td className="py-2 pr-4">
                {i.buyerName}
                <span className="ml-1 text-xs text-subtle-ink">{i.customerType}</span>
              </td>
              <td className="py-2 pr-4 text-muted">{i.periodStart} → {i.periodEnd}</td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {i.grossTotal.toFixed(2)} {i.currency}
                {/* Zero VAT means something different in each case, so it is spelled out. */}
                {i.vatTotal === 0 && (
                  <div className="text-xs text-subtle-ink">
                    {i.vatTreatment === 'NotVatRegistered' ? 'no VAT — not registered'
                      : i.vatTreatment === 'ReverseCharge' ? 'reverse charge'
                        : i.vatTreatment === 'Exempt' ? 'outside scope' : 'no VAT'}
                  </div>
                )}
              </td>
              <td className="py-2 pr-4">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[i.status]}`}>
                  {statusLabels(t)[i.status]}
                </span>
                {i.provider && <div className="mt-0.5 text-xs text-subtle-ink">via {i.provider}</div>}
              </td>
              <td className="py-2 text-right">
                <div className="flex justify-end gap-1.5">
                  {i.status === 'Draft' && (
                    <Button size="sm" loading={busy === i.id}
                      onClick={() => onAct(i.id, () => issuePlatformInvoice(i.id))}>
                      Issue
                    </Button>
                  )}
                  {(i.status === 'Issued' || i.status === t('billing.failed', "Failed")) && (
                    <Button size="sm" variant="secondary" loading={busy === i.id}
                      onClick={() => onAct(i.id, () => filePlatformInvoice(i.id))}>
                      File
                    </Button>
                  )}
                  {i.hasPdf && (
                    <a className="rounded-lg border border-line-strong px-2.5 py-1 text-xs font-medium text-muted-strong hover:bg-canvas"
                      href={`/api/v1/admin/platform-invoices/${i.id}/pdf`} target="_blank" rel="noreferrer">
                      PDF
                    </a>
                  )}
                  {i.hasXml && (
                    <a className="rounded-lg border border-line-strong px-2.5 py-1 text-xs font-medium text-muted-strong hover:bg-canvas"
                      href={`/api/v1/admin/platform-invoices/${i.id}/xml`} target="_blank" rel="noreferrer">
                      XML
                    </a>
                  )}
                  {/* Issued and beyond: the document is out, so it is corrected rather than undone. */}
                  {i.kind === 'Invoice'
                    && ['Issued', 'Submitted', 'Filed', 'Rejected'].includes(i.status) && (
                    <button
                      className="rounded-lg border border-line-strong px-2.5 py-1 text-xs font-medium text-muted hover:bg-canvas"
                      onClick={() => {
                        const reason = window.prompt(t('billing.whatCorrected', 'What is being corrected?'));
                        if (!reason) return;
                        const part = window.prompt(
                          `Amount to credit, or leave blank for the whole ${i.grossTotal.toFixed(2)} ${i.currency}.`);
                        const amount = part?.trim() ? Number(part) : undefined;
                        if (amount !== undefined && !Number.isFinite(amount)) return;
                        void onAct(i.id, () => creditPlatformInvoice(i.id, reason, amount));
                      }}
                    >
                      Credit
                    </button>
                  )}
                  {(i.status === 'Draft' || i.status === 'AwaitingCustomerDetails') && (
                    <button
                      className="rounded-lg border border-line-strong px-2.5 py-1 text-xs font-medium text-muted hover:bg-canvas"
                      onClick={() => {
                        const reason = window.prompt(t('billing.whyWithdrawn', 'Why is this being withdrawn?'));
                        if (reason) void onAct(i.id, () => cancelPlatformInvoice(i.id, reason));
                      }}
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              </td>
            </tr>
            {(i.blockedReason || i.error) && (
              <tr>
                <td colSpan={6} className="pb-2 text-xs text-amber-700">
                  {i.blockedReason ?? i.error}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
  );
};

export default BillingPage;
